import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';

const TIPO_COLOR = { cobro: '#34d399', credito: '#818cf8', vencimiento: '#f87171', sistema: '#fbbf24', info: '#64748b' };
const TIPO_ICON  = { cobro: '💰', credito: '📋', vencimiento: '⚠', sistema: '⚙', info: 'ℹ' };

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr + 'Z')) / 1000);
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function NotificacionesBell() {
  const [notifs, setNotifs] = useState([]);
  const [noLeidas, setNoLeidas] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/notificaciones');
      setNotifs(data.notificaciones);
      setNoLeidas(data.no_leidas);
    } catch (_) {}
  }, []);

  useEffect(() => {
    cargar();
    const interval = setInterval(cargar, 30000);
    return () => clearInterval(interval);
  }, [cargar]);

  // Cerrar al hacer clic afuera
  useEffect(() => {
    function onClickOut(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  async function marcarLeida(id) {
    await api.put(`/notificaciones/${id}/leer`);
    setNotifs(p => p.map(n => n.id === id ? { ...n, leida: 1 } : n));
    setNoLeidas(p => Math.max(0, p - 1));
  }

  async function marcarTodas() {
    await api.put('/notificaciones/leer-todas');
    setNotifs(p => p.map(n => ({ ...n, leida: 1 })));
    setNoLeidas(0);
  }

  async function eliminar(id, e) {
    e.stopPropagation();
    await api.delete(`/notificaciones/${id}`);
    setNotifs(p => p.filter(n => n.id !== id));
    const era = notifs.find(n => n.id === id);
    if (era && !era.leida) setNoLeidas(p => Math.max(0, p - 1));
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Botón campanita */}
      <button
        onClick={() => { setOpen(o => !o); if (!open) cargar(); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '6px 8px', borderRadius: 8, color: 'var(--text-2)', display: 'flex', alignItems: 'center' }}
        title="Notificaciones"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {noLeidas > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2,
            background: '#f87171', color: '#fff',
            borderRadius: '50%', width: 16, height: 16,
            fontSize: 10, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1,
          }}>
            {noLeidas > 9 ? '9+' : noLeidas}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0,
          width: 340, maxHeight: 480,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 12, boxShadow: '0 8px 32px #00000060',
          zIndex: 1000, display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, color: 'var(--text-1)', fontSize: 14 }}>Notificaciones {noLeidas > 0 && <span style={{ color: '#f87171' }}>({noLeidas})</span>}</span>
            {noLeidas > 0 && (
              <button onClick={marcarTodas} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#6366f1' }}>
                Marcar todas leídas
              </button>
            )}
          </div>

          {/* Lista */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {notifs.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-4)', fontSize: 13 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
                Sin notificaciones
              </div>
            ) : notifs.map(n => (
              <div
                key={n.id}
                onClick={() => !n.leida && marcarLeida(n.id)}
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--border-2)',
                  background: n.leida ? 'transparent' : '#0f172a40',
                  cursor: n.leida ? 'default' : 'pointer',
                  display: 'flex', gap: 12, alignItems: 'flex-start',
                  transition: 'background 0.15s',
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: `${TIPO_COLOR[n.tipo] || '#64748b'}22`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
                }}>
                  {TIPO_ICON[n.tipo] || 'ℹ'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: n.leida ? 400 : 600, color: 'var(--text-1s)', marginBottom: 2 }}>{n.titulo}</div>
                  {n.mensaje && <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.4 }}>{n.mensaje}</div>}
                  <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 4 }}>{timeAgo(n.created_at)}</div>
                </div>
                <button
                  onClick={(e) => eliminar(n.id, e)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-4)', fontSize: 14, padding: '0 2px', flexShrink: 0 }}
                  title="Eliminar"
                >✕</button>
                {!n.leida && (
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#6366f1', flexShrink: 0, marginTop: 4 }} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
